import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ShieldCheck } from "lucide-react";
import { http, edition, gradeLabel } from "@/lib/api";
import { StateTabs } from "@/pages/admin/Library";
import { Lock } from "lucide-react";
import { COPYRIGHT } from "@/lib/brand";

export default function CurriculumLibrary() {
  const [d, setD] = useState(null);
  const [st, setSt] = useState(edition.state);
  useEffect(() => { http.get("/public/library", { params: { state: st } }).then((r) => setD(r.data)); }, [st]);
  if (!d) return <p className="text-center py-20 text-stone-500">Loading…</p>;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="curriculum-page">
      <p className="text-xs uppercase tracking-[0.25em] text-[#C87D53] font-semibold">For families, schools & accreditors</p>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] mt-2">Our Curriculum</h1>
      <p className="text-stone-600 mt-3 max-w-2xl">An original, consciousness-based K-12 program aligned to your state's standards. Every document below is reviewed and approved, and each version is recorded.</p>
      <div className="mt-6"><StateTabs value={st} onChange={setSt} testId="curriculum-state-tabs" /></div>
      {Object.entries(d.sections).map(([sec, name]) => {
        const docs = d.docs.filter((x) => x.section === sec);
        if (!docs.length) return null;
        return (
          <section key={sec} className="mt-12">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-stone-500">{name}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {docs.map((x) => (
                <Link key={x.key} to={`/curriculum/${encodeURIComponent(x.key)}`} data-testid={`curriculum-doc-${x.doc}`}
                  className="rounded-3xl p-5 bg-white/70 border border-[#E6DEC3] hover:border-[#2C4A3E] transition-colors">
                  <FileText className="w-5 h-5 text-[#C87D53]" />
                  <p className="font-display text-lg text-[#2C4A3E] mt-3">{x.title}</p>
                  <p className="text-xs text-stone-500 mt-1">{x.summary}</p>
                  <p className="text-xs text-stone-400 mt-3">Version {x.version} · approved {new Date(x.approved_at).toLocaleDateString()}</p>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
      {d.courses.length > 0 && (
        <section className="mt-12" data-testid="curriculum-courses">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-stone-500">Course documents</h2>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {d.courses.map(({ course, docs }) => (
              <div key={course.id} className="rounded-3xl p-5 bg-white/70 border border-[#E6DEC3]" data-testid={`curriculum-course-${course.id}`}>
                <p className="text-[10px] uppercase tracking-widest text-stone-500">{course.subject_name} · {gradeLabel(course.grade)}</p>
                <p className="font-display text-lg text-[#2C4A3E]">{course.title}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {docs.map((x) => (
                    <Link key={x.key} to={`/curriculum/${encodeURIComponent(x.key)}`} data-testid={`curriculum-course-doc-${course.id}-${x.doc}`}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F7F3EB] text-xs text-[#2C4A3E] hover:bg-[#F1EADB]">
                      {x.visibility === "private" && <Lock className="w-3 h-3" />}{x.title.split(": ").pop()}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      {!d.docs.length && !d.courses.length && <p className="text-center text-stone-500 py-16" data-testid="curriculum-empty">Curriculum documents are being prepared. Please check back soon.</p>}
      {d.revision_log.length > 0 && (
        <section className="mt-16" data-testid="curriculum-revision-log">
          <h2 className="font-display text-2xl text-[#2C4A3E] inline-flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#6B9080]" />Version & revision log</h2>
          <div className="mt-4 overflow-x-auto rounded-3xl border border-[#E6DEC3] bg-white/60">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-widest text-stone-500 border-b border-[#E6DEC3]"><th className="px-5 py-3">Document</th><th>Version</th><th>Change</th><th>Approved</th></tr></thead>
              <tbody>
                {d.revision_log.map((r) => (
                  <tr key={`${r.key}-${r.version}`} className="border-b border-[#E6DEC3]/60 last:border-0">
                    <td className="px-5 py-3 text-[#2C4A3E] font-medium">{r.title}</td><td>v{r.version}</td><td className="text-stone-600">{r.change_note}</td>
                    <td className="text-stone-500">{new Date(r.approved_at).toLocaleDateString()} · {r.approved_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <p className="text-xs text-stone-400 text-center mt-16">{COPYRIGHT}</p>
    </div>
  );
}
